import React, { useState } from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import useFilterStore from '../store/filterStore';

/**
 * Left sidebar filter component for JobBoard
 * Always visible on desktop, collapsible sections
 */
const JobFilters = ({ className = '' }) => {
  const {
    filters,
    setFilter,
    toggleFilterValue,
    clearAllFilters,
    activeFilterCount
  } = useFilterStore();

  const [expandedSections, setExpandedSections] = useState({
    experienceLevel: true,
    jobType: true,
    projectLength: true,
    budget: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className={`bg-white rounded-lg border border-gray-200 shadow-sm ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900">Filters</h3>
        {activeFilterCount > 0 && (
          <button
            onClick={clearAllFilters}
            className="text-xs font-medium text-primary-600 hover:text-primary-700"
          >
            Clear all ({activeFilterCount})
          </button>
        )}
      </div>

      {/* Filter Sections */}
      <div className="divide-y divide-gray-100">
        {/* Experience Level */}
        <FilterSection
          title="Experience Level"
          isExpanded={expandedSections.experienceLevel}
          onToggle={() => toggleSection('experienceLevel')}
        >
          <div className="space-y-2">
            {[
              { value: 'entry', label: 'Entry Level' },
              { value: 'intermediate', label: 'Intermediate' },
              { value: 'expert', label: 'Expert' }
            ].map((option) => (
              <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.experienceLevels.includes(option.value)}
                  onChange={() => toggleFilterValue('experienceLevels', option.value)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
        </FilterSection>

        {/* Job Type */}
        <FilterSection
          title="Job Type"
          isExpanded={expandedSections.jobType}
          onToggle={() => toggleSection('jobType')}
        >
          <div className="space-y-2">
            {[
              { value: 'hourly', label: 'Hourly' },
              { value: 'fixed', label: 'Fixed-Price' }
            ].map((option) => (
              <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.jobType.includes(option.value)}
                  onChange={() => toggleFilterValue('jobType', option.value)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
        </FilterSection>

        {/* Project Length */}
        <FilterSection
          title="Project Length"
          isExpanded={expandedSections.projectLength}
          onToggle={() => toggleSection('projectLength')}
        >
          <div className="space-y-2">
            {[
              { value: '<1month', label: 'Less than one month' },
              { value: '1-3months', label: '1 to 3 months' },
              { value: '3-6months', label: '3 to 6 months' },
              { value: '6+months', label: 'More than 6 months' }
            ].map((option) => (
              <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.projectLength.includes(option.value)}
                  onChange={() => toggleFilterValue('projectLength', option.value)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
        </FilterSection>

        {/* Budget Range */}
        <FilterSection
          title="Budget"
          isExpanded={expandedSections.budget}
          onToggle={() => toggleSection('budget')}
        >
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                <input
                  type="number"
                  value={filters.budgetMin}
                  onChange={(e) => setFilter('budgetMin', e.target.value)}
                  placeholder="Min"
                  className="w-full pl-6 pr-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
            <span className="text-gray-400">-</span>
            <div className="flex-1">
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                <input
                  type="number"
                  value={filters.budgetMax}
                  onChange={(e) => setFilter('budgetMax', e.target.value)}
                  placeholder="Max"
                  className="w-full pl-6 pr-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>
        </FilterSection>
      </div>
    </div>
  );
};

/**
 * Filter Section Component
 */
const FilterSection = ({ title, isExpanded, onToggle, children }) => {
  return (
    <div className="px-4 py-3">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between text-left mb-2"
      >
        <span className="text-sm font-medium text-gray-900">{title}</span>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        )}
      </button>
      {isExpanded && children}
    </div>
  );
};

export default JobFilters;
